/**
 * RenderMode context — `'interactive'` (the live dashboard) or
 * `'print'` (a `/print/...` route the headless browser is rendering).
 *
 * Components that should behave differently in print (no animations,
 * static expansion, pagination-safe layouts, print-only headers,
 * different chart resolutions) consult this via `usePrintMode()`. The
 * default is `'interactive'` so any component used outside a print
 * route keeps its live-dashboard behavior with zero changes.
 *
 * Introduced early (#783 Slice 1) — cheap now, painful to retrofit
 * later.
 */
import { createContext, useContext, type ReactNode } from 'react'

export type RenderMode = 'interactive' | 'print'

const RenderModeContext = createContext<RenderMode>('interactive')

export function RenderModeProvider({
  value,
  children,
}: {
  value: RenderMode
  children: ReactNode
}) {
  return (
    <RenderModeContext.Provider value={value}>
      {children}
    </RenderModeContext.Provider>
  )
}

/** Current render mode. `'interactive'` by default. */
export function useRenderMode(): RenderMode {
  return useContext(RenderModeContext)
}

/** True iff we're inside a print render. */
export function usePrintMode(): boolean {
  return useContext(RenderModeContext) === 'print'
}
